import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import toast from 'react-hot-toast'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import {
  ArrowLeft,
  BookOpen,
  Map,
  Users,
  Play,
  Edit,
  Trash2,
  Plus,
  Eye,
  FileSpreadsheet,
  FileText,
  Loader2,
  BarChart2,
  ImagePlus,
  X,
  Clock,
  GripVertical,
  AlertTriangle,
  Coins,
  CheckCircle,
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { checkoutService } from '@/services/checkout'
import { useIsAdmin, useIsCaptain, useAuthStore, useUserRole } from '@/store/authStore'
import { useTerminology } from '@/hooks/useTerminology'
import pirateStyles from './CourseDetail.module.css'

import type { Course, CourseRun, Lesson, LessonActivity, DetailedPlayerAnalytics } from '@/types'
import {
  generateCourseExcelReport,
  generateCoursePdfReport,
  getCurrentPdfTheme,
  type CourseReportData,
  type LessonReportData,
  type RawResponse,
} from '@/utils/reportGenerator'

export function CourseDetail() {
  const { organizationId, courseId } = useParams<{ organizationId: string; courseId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddLesson, setShowAddLesson] = useState(false)
  const isAdmin = useIsAdmin()
  const isCaptain = useIsCaptain()
  const role = useUserRole()
  const isPirate = role === 'pirate'
  const user = useAuthStore((s) => s.user)
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const creditBalance = user?.credit_balance ?? 0
  const t = useTerminology()
  const [enrolling, setEnrolling] = useState(false)
  const [showPirateCreditsModal, setShowPirateCreditsModal] = useState(false)
  const [runningCourse, setRunningCourse] = useState(false)
  const [myRuns, setMyRuns] = useState<CourseRun[]>([])
  const [selectedRun, setSelectedRun] = useState<CourseRun | null>(null)
  const [showNewRunModal, setShowNewRunModal] = useState(false)
  const [newRunTitle, setNewRunTitle] = useState('')
  const [creatingRun, setCreatingRun] = useState(false)
  const [activeTab, setActiveTab] = useState<'lessons' | 'settings'>('lessons')
  const [excelLoading, setExcelLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [reportProgress, setReportProgress] = useState('')
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editCreditCost, setEditCreditCost] = useState(0)
  const [editSelfPacedCreditCost, setEditSelfPacedCreditCost] = useState(0)
  const [editTier25Cost, setEditTier25Cost] = useState(0)
  const [editTier50Cost, setEditTier50Cost] = useState(0)
  const [editTier100Cost, setEditTier100Cost] = useState(0)
  const [editTier250Cost, setEditTier250Cost] = useState(0)
  const [editDuration, setEditDuration] = useState<number | ''>('')
  const [editImageFile, setEditImageFile] = useState<File | null>(null)
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null)
  const [savingSettings, setSavingSettings] = useState(false)

  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showTierModal, setShowTierModal] = useState(false)
  const [selectedTier, setSelectedTier] = useState<number | null>(null)
  const [showCloneSuccessModal, setShowCloneSuccessModal] = useState(false)
  const [clonedCourseId, setClonedCourseId] = useState<string | null>(null)
  const [showDeleteRunModal, setShowDeleteRunModal] = useState(false)
  const [deleteRunTarget, setDeleteRunTarget] = useState<{ id: string; title: string; status: string } | null>(null)
  const [deletingRun, setDeletingRun] = useState(false)
  const [editingRunTitle, setEditingRunTitle] = useState(false)
  const [editRunTitleValue, setEditRunTitleValue] = useState('')
  const [savingRunTitle, setSavingRunTitle] = useState(false)

  const [dragId, setDragId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [reorderSaving, setReorderSaving] = useState(false)


  const refreshPaymentMethod = () => {
    checkoutService.getBalance()
      .then(b => setHasPaymentMethod(b.has_payment_method))
      .catch(() => {})
  }

  useEffect(() => {
    if (courseId && organizationId) loadCourse()
    refreshPaymentMethod()
    // Re-check after a delay when returning from payment method setup (Stripe propagation)
    const returnedFromPayment = (location.state as any)?.returnTo || location.key
    const timer = setTimeout(refreshPaymentMethod, 2000)
    return () => clearTimeout(timer)
  }, [courseId, organizationId, location.key])

  useEffect(() => {
    if (courseId && organizationId && isCaptain && course) {
      loadMyRuns()
    }
  }, [courseId, organizationId, isCaptain, course?.id])

  const loadMyRuns = async () => {
    try {
      const res = await apiClient.getMyRuns(organizationId!, courseId!)
      const runs = (res.data as any) ?? []
      const runsList = Array.isArray(runs) ? runs : []
      setMyRuns(runsList)
      // Auto-select the first run if none is selected
      if (!selectedRun && runsList.length > 0) {
        setSelectedRun(runsList[0])
      }
    } catch {
      setMyRuns([])
    }
  }

  const loadCourse = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getCourse(organizationId!, courseId!)
      const data = (response.data as any)?.course ?? response.data
      setCourse(data)
      setEditName(data?.name ?? '')
      setEditDesc(data?.description ?? '')
      setEditCreditCost(data?.credit_cost ?? 0)
      setEditSelfPacedCreditCost(data?.self_paced_credit_cost ?? 0)
      setEditTier25Cost(data?.tier_25_cost ?? 0)
      setEditTier50Cost(data?.tier_50_cost ?? 0)
      setEditTier100Cost(data?.tier_100_cost ?? 0)
      setEditTier250Cost(data?.tier_250_cost ?? 0)
      setEditDuration(data?.estimated_duration_hours ?? '')
      setEditImagePreview(data?.image_url ?? null)
      setEditImageFile(null)
    } catch (err: any) {
      setError(handleApiError(err, 'load course', false))
    } finally {
      setLoading(false)
    }
  }

  const handlePirateEnroll = async () => {
    if (!course) return
    setEnrolling(true)
    try {
      await apiClient.enrollInCourseSelfPaced(course.course_code)
      setCourse(prev => prev ? { ...prev, enrolled: true, enrolled_count: prev.enrolled_count + 1 } : prev)
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setShowPirateCreditsModal(true)
      } else {
        handleApiError(err, 'enroll in course')
      }
    } finally {
      setEnrolling(false)
    }
  }

  const handleRunCourse = async () => {
    if (!organizationId || !courseId || !course) return

    if (course.source_course_id) {
      // Already viewing a cloned course — open the new run modal
      setShowNewRunModal(true)
      return
    }

    // Source course — clone the shell first (idempotent), then navigate
    setRunningCourse(true)
    try {
      const response = await apiClient.runCourse(organizationId, courseId)
      const clonedCourse = (response.data as any)?.course ?? response.data

      setClonedCourseId(clonedCourse.already_cloned ? null : clonedCourse.id)
      setShowCloneSuccessModal(true)
    } catch (err: any) {
      handleApiError(err, 'run course')
    } finally {
      setRunningCourse(false)
    }
  }

  const handleCreateRun = async (tier?: number) => {
    if (!organizationId || !courseId || !newRunTitle.trim()) return
    setCreatingRun(true)
    try {
      const response = await apiClient.createCourseRun(organizationId, courseId, newRunTitle.trim(), tier)
      const run = (response.data as any)?.course_run ?? response.data
      showSuccessToast(`Run "${run.title}" created!`)
      setShowNewRunModal(false)
      setShowTierModal(false)
      setNewRunTitle('')
      setSelectedTier(null)
      await loadMyRuns()
      setSelectedRun(run)
      // Refresh user profile to update credit_balance in navbar
      checkAuth()
    } catch (err: any) {
      handleApiError(err, 'create run')
    } finally {
      setCreatingRun(false)
    }
  }

  const handleDeleteRun = (runId: string, runTitle: string, runStatus: string) => {
    setDeleteRunTarget({ id: runId, title: runTitle, status: runStatus })
    setShowDeleteRunModal(true)
  }

  const confirmDeleteRun = async () => {
    if (!deleteRunTarget) return
    setDeletingRun(true)
    try {
      await apiClient.deleteCourseRun(organizationId!, courseId!, deleteRunTarget.id)
      showSuccessToast(`Run "${deleteRunTarget.title}" deleted`)
      setSelectedRun(null)
      setShowDeleteRunModal(false)
      setDeleteRunTarget(null)
      await loadMyRuns()
    } catch (err: any) {
      handleApiError(err, 'delete run')
    } finally {
      setDeletingRun(false)
    }
  }

  const handleRenameRun = async () => {
    if (!selectedRun || !editRunTitleValue.trim()) return
    setSavingRunTitle(true)
    try {
      const res = await apiClient.updateCourseRun(organizationId!, courseId!, selectedRun.id, editRunTitleValue.trim())
      if (res.success) {
        showSuccessToast('Run renamed')
        setEditingRunTitle(false)
        setSelectedRun({ ...selectedRun, title: editRunTitleValue.trim() })
        await loadMyRuns()
      }
    } catch (err: any) {
      handleApiError(err, 'rename run')
    } finally {
      setSavingRunTitle(false)
    }
  }

  const handleRemoveLesson = async (lessonId: string) => {
    if (!confirm(`Remove this ${t.lesson.lower} from the ${t.course.lower}?`)) return
    try {
      await apiClient.removeLessonFromCourse(organizationId!, courseId!, lessonId)
      showSuccessToast(`${t.lesson.title} removed from ${t.course.lower}`)
      await loadCourse()
    } catch (err: any) {
      handleApiError(err, 'remove lesson')
    }
  }

  const handleDragStart = (e: React.DragEvent | MouseEvent | TouchEvent | PointerEvent, lessonId: string) => {
    setDragId(lessonId)
    if ('dataTransfer' in e) {
      e.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (lessonId: string) => {
    if (lessonId !== dragId) setDragOverId(lessonId)
  }

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!dragId || dragId === targetId || !course) return

    const oldLessons = [...(course.lessons ?? [])]
    const dragIdx = oldLessons.findIndex(l => l.id === dragId)
    const targetIdx = oldLessons.findIndex(l => l.id === targetId)
    if (dragIdx === -1 || targetIdx === -1) return

    // Reorder optimistically
    const reordered = [...oldLessons]
    const [moved] = reordered.splice(dragIdx, 1)
    reordered.splice(targetIdx, 0, moved)
    setCourse(prev => prev ? { ...prev, lessons: reordered } : prev)

    setDragId(null)
    setDragOverId(null)

    // Save to backend
    try {
      setReorderSaving(true)
      await apiClient.reorderCourseLessons(organizationId!, courseId!, reordered.map(l => l.id))
    } catch (err: any) {
      handleApiError(err, 'reorder lessons')
      setCourse(prev => prev ? { ...prev, lessons: oldLessons } : prev)
    } finally {
      setReorderSaving(false)
    }
  }

  const handleDragEnd = () => {
    setDragId(null)
    setDragOverId(null)
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingSettings(true)
    try {
      const fd = new FormData()
      fd.append('course[name]', editName)
      fd.append('course[description]', editDesc)
      fd.append('course[credit_cost]', String(editCreditCost))
      fd.append('course[self_paced_credit_cost]', String(editSelfPacedCreditCost))
      fd.append('course[tier_25_cost]', String(editTier25Cost))
      fd.append('course[tier_50_cost]', String(editTier50Cost))
      fd.append('course[tier_100_cost]', String(editTier100Cost))
      fd.append('course[tier_250_cost]', String(editTier250Cost))
      fd.append('course[estimated_duration_hours]', editDuration === '' ? '' : String(editDuration))
      if (editImageFile) {
        fd.append('course[image]', editImageFile)
      }
      await apiClient.request('patch', `/organizations/${organizationId}/courses/${courseId}`, fd)
      showSuccessToast(`${t.course.title} updated!`)
      await loadCourse()
    } catch (err: any) {
      handleApiError(err, 'update course')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleDeleteCourse = async () => {
    if (!confirm(`Are you sure you want to delete this ${t.course.lower}? This cannot be undone.`)) return
    try {
      await apiClient.deleteCourse(organizationId!, courseId!)
      showSuccessToast(`${t.course.title} deleted`)
      navigate(`/organizations/${organizationId}/courses`)
    } catch (err: any) {
      handleApiError(err, 'delete course')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[var(--color-text-muted)] font-mono">LOADING COURSE</p>
        </div>
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error ?? `${t.course.title} not found`}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-[var(--color-input-bg)] rounded-lg text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const lessons: Lesson[] = course.lessons ?? []

  const fetchCourseReportData = async (): Promise<CourseReportData> => {
    const courseRes = await apiClient.getCourseOverview(courseId!)
    const overview = courseRes.data as any
    const lessonData: LessonReportData[] = []
    const breakdown = overview?.lessonBreakdown ?? []

    for (let i = 0; i < breakdown.length; i++) {
      const lb = breakdown[i]
      setReportProgress(`Loading lesson ${i + 1}/${breakdown.length}...`)

      const [lessonRes, analyticsRes] = await Promise.allSettled([
        apiClient.getLesson(lb.lessonId),
        apiClient.getLessonPlayersAnalytics(lb.lessonId),
      ])

      const fullLesson: Lesson =
        lessonRes.status === 'fulfilled'
          ? ((lessonRes.value.data as any)?.lesson ?? lessonRes.value.data)
          : { id: lb.lessonId, name: lb.lessonName, activities: [], players: [] } as any
      const activities: LessonActivity[] = fullLesson.activities ?? []
      const players = fullLesson.players ?? []

      const rawAnalytics: DetailedPlayerAnalytics[] =
        analyticsRes.status === 'fulfilled'
          ? (analyticsRes.value.data as any)?.players ?? analyticsRes.value.data ?? []
          : []
      const playerAnalytics: Record<string, DetailedPlayerAnalytics> = {}
      for (const a of rawAnalytics) playerAnalytics[a.id] = a

      const responseResults = await Promise.allSettled(
        activities.map(act => apiClient.getResponses(lb.lessonId, act.id))
      )
      const activityResponses: Record<string, RawResponse[]> = {}
      activities.forEach((act, idx) => {
        const result = responseResults[idx]
        activityResponses[act.id] = result.status === 'fulfilled'
          ? (result.value.data as any)?.responses ?? result.value.data ?? []
          : []
      })

      lessonData.push({ lesson: fullLesson, activities, activityResponses, players, playerAnalytics })
    }
    setReportProgress('')
    return { course, lessons: lessonData }
  }

  const handleDownloadExcel = async () => {
    setExcelLoading(true)
    try {
      const data = await fetchCourseReportData()
      await generateCourseExcelReport(data)
    } catch (e) {}
    finally { setExcelLoading(false); setReportProgress('') }
  }

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      const data = await fetchCourseReportData()
      await generateCoursePdfReport(data, getCurrentPdfTheme())
    } catch (e) {}
    finally { setPdfLoading(false); setReportProgress('') }
  }

  const reportLoading = excelLoading || pdfLoading

  // Captain viewing an admin/original course they haven't cloned yet
  const isCatalogView = isCaptain && !isAdmin && !course.source_course_id

  // ─── Pirate Preview View ──────────────────────────────────────────────
  if (isPirate) {
    const cost = course.self_paced_credit_cost ?? course.credit_cost ?? 0
    return (
      <div className={pirateStyles.piratePreview}>
        <div className={pirateStyles.pirateContainer}>
          <button className={pirateStyles.backButton} onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {course.image_url ? (
            <img src={course.image_url} alt={course.name} className={pirateStyles.heroImage} />
          ) : (
            <div className={pirateStyles.heroPlaceholder}>
              <BookOpen className="w-12 h-12" />
            </div>
          )}

          <h1 className={pirateStyles.courseTitle}>{course.name}</h1>
          {course.instructor_name && (
            <p className={pirateStyles.instructorName}>by {course.instructor_name}</p>
          )}

          {course.description && (
            <p className={pirateStyles.courseDescription}>{course.description}</p>
          )}

          <div className={pirateStyles.statsRow}>
            <div className={pirateStyles.statBadge}>
              <BookOpen />
              <div>
                <div className={pirateStyles.statValue}>{lessons.length}</div>
                <div className={pirateStyles.statLabel}>{t.lesson.lowerPlural}</div>
              </div>
            </div>
            {course.estimated_duration_hours ? (
              <div className={pirateStyles.statBadge}>
                <Clock />
                <div>
                  <div className={pirateStyles.statValue}>~{course.estimated_duration_hours}h</div>
                  <div className={pirateStyles.statLabel}>duration</div>
                </div>
              </div>
            ) : null}
            {isAdmin && (
              <div className={pirateStyles.statBadge}>
                <Users />
                <div>
                  <div className={pirateStyles.statValue}>{course.instructor_count ?? course.enrolled_count}</div>
                  <div className={pirateStyles.statLabel}>captains aboard</div>
                </div>
              </div>
            )}
          </div>

          {lessons.length > 0 && (
            <div className={pirateStyles.lessonListSection}>
              <h2 className={pirateStyles.lessonListTitle}>What You'll Learn</h2>
              <div className={pirateStyles.lessonList}>
                {lessons.map((lesson, idx) => (
                  <div key={lesson.id} className={pirateStyles.lessonRow}>
                    <span className={pirateStyles.lessonNumber}>{idx + 1}</span>
                    <span className={pirateStyles.lessonName}>{lesson.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={pirateStyles.costSection}>
            <span className={pirateStyles.costBadge} data-free={String(cost === 0)}>
              {cost > 0 ? `${cost} credits` : 'FREE'}
            </span>
          </div>

          {course.enrolled ? (
            <button
              className={pirateStyles.actionButton}
              onClick={() => navigate(`/course/${course.course_code}`)}
            >
              Continue {t.course.upper}
            </button>
          ) : (
            <button
              className={pirateStyles.actionButton}
              disabled={enrolling}
              onClick={handlePirateEnroll}
            >
              {enrolling ? 'ENROLLING...' : 'ENROLL'}
            </button>
          )}
        </div>

        {/* Insufficient Credits Modal */}
        {showPirateCreditsModal && (
          <div className={pirateStyles.creditsModal} role="dialog" aria-modal="true" aria-labelledby="credits-modal-title">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={pirateStyles.creditsModalContent}
            >
              <div className={pirateStyles.creditsModalHeader}>
                <div className={pirateStyles.creditsModalIcon}>
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h3 id="credits-modal-title" className={pirateStyles.creditsModalTitle}>Insufficient Credits</h3>
              </div>
              <p className={pirateStyles.creditsModalText}>
                You don't have enough credits to enrol in this {t.course.lower}. Purchase more credits to continue.
              </p>
              <div className={pirateStyles.creditsModalActions}>
                <button
                  onClick={() => setShowPirateCreditsModal(false)}
                  className={pirateStyles.creditsModalCancel}
                >
                  Cancel
                </button>
                <button
                  onClick={() => navigate('/users/plans')}
                  className={pirateStyles.creditsModalBuy}
                >
                  Buy Credits
                </button>
              </div>
            </motion.div>
          </div>
        )}

      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      {/* Page header */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-[var(--color-input-bg)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold truncate text-green-500">{course.name}</h1>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase shrink-0 ${
                  course.status === 'active' ? 'bg-green-500/15 text-green-500' :
                  course.status === 'archived' ? 'bg-[var(--color-input-bg)] text-[var(--color-text-muted)]' :
                  'bg-yellow-500/15 text-yellow-500'
                }`}>
                  {course.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-[var(--color-text-muted)] flex-wrap">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-3.5 h-3.5" />
                  {course.lesson_count ?? lessons.length} {t.lesson.lowerPlural}
                </span>
                {isAdmin && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {course.instructor_count ?? 0} captains aboard
                  </span>
                )}
                {course.instructor_name && (
                  <span className="flex items-center gap-1">
                    <span className="text-[var(--color-text-muted)]">by</span>
                    <span className={course.source_course_id ? 'text-[var(--color-secondary)]' : 'text-[var(--color-primary)]'}>
                      {course.instructor_name}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Report download buttons */}
            {!isCatalogView && myRuns.some(r => r.status === 'completed') && (
              <div className="flex items-center gap-2 shrink-0">
                {reportLoading && reportProgress && (
                  <span className="text-xs text-blue-400 mr-1 hidden sm:inline">{reportProgress}</span>
                )}
                <button
                  onClick={handleDownloadExcel}
                  disabled={reportLoading}
                  title="Download Excel Report"
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg hover:bg-green-500/20 hover:border-green-500/50 transition-all text-green-600 dark:text-green-400 text-sm font-medium disabled:opacity-50"
                >
                  {excelLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Excel</span>
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={reportLoading}
                  title="Download PDF Report"
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 hover:border-red-500/50 transition-all text-red-600 dark:text-red-400 text-sm font-medium disabled:opacity-50"
                >
                  {pdfLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">PDF</span>
                </button>
                <button
                  onClick={() => navigate(`/organizations/${organizationId}/courses/${courseId}/report`)}
                  title="View Full Report"
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 hover:border-blue-500/50 transition-all text-blue-600 dark:text-blue-400 text-sm font-medium"
                >
                  <BarChart2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Report</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            {/* Run Course (captain viewing source or cloned course) */}
            {isCaptain && !isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-border)] space-y-3"
              >
                {course.source_course_id ? (
                  <>
                    <h2 className="text-sm font-semibold text-green-500 uppercase tracking-wide">
                      Your Runs
                    </h2>

                    {myRuns.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {myRuns.map(run => (
                          <button
                            key={run.id}
                            onClick={() => { if (selectedRun?.id !== run.id) setSelectedRun(run) }}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors text-left ${
                              selectedRun?.id === run.id ? 'bg-[var(--color-primary-subtle)] border border-[var(--color-primary)]' : 'bg-[var(--color-input-bg)] hover:bg-[var(--color-surface-hover)]'
                            }`}
                          >
                            <Play className={`w-3.5 h-3.5 shrink-0 ${
                              run.status === 'started' ? 'text-yellow-600 dark:text-yellow-400' : run.status === 'completed' ? 'text-[var(--color-text-muted)]' : 'text-yellow-600 dark:text-yellow-400'
                            }`} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-[var(--color-text)] truncate">{run.title}</div>
                              <div className="text-xs text-[var(--color-text-muted)] font-mono flex items-center gap-2">
                                <span>{run.enrolled_count ?? 0} students</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                                  run.status === 'draft' ? 'bg-yellow-500/10 text-yellow-500'
                                  : run.status === 'started' ? 'bg-yellow-500/10 text-yellow-500'
                                  : run.status === 'completed' ? 'bg-green-500/10 text-green-500'
                                  : 'bg-red-500/10 text-red-500'
                                }`}>{run.status}</span>
                              </div>
                              {run.run_code && (
                                <div className="text-[10px] text-[var(--color-text-muted)] font-mono mt-0.5">
                                  Code: {run.run_code}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => setShowNewRunModal(true)}
                      disabled={creatingRun}
                      data-tour="new-run-btn"
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-primary-subtle)] text-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary-glow)] transition-colors text-sm font-mono disabled:opacity-50"
                    >
                      {creatingRun ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      {creatingRun ? 'CREATING...' : 'NEW RUN'}
                    </button>
                    <p className="text-xs text-[var(--color-text-muted)] text-center">
                      Each run gets fresh lessons and a unique join code
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
                      Run This Course
                    </h2>
                    <button
                      onClick={handleRunCourse}
                      disabled={runningCourse}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-primary-subtle)] text-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary-glow)] transition-colors text-sm font-mono disabled:opacity-50"
                    >
                      {runningCourse ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      {runningCourse ? 'SETTING UP...' : 'RUN COURSE'}
                    </button>
                    <p className="text-xs text-[var(--color-text-muted)] text-center">
                      Creates your own copy where you can manage runs
                    </p>
                  </>
                )}
              </motion.div>
            )}

            {/* Course actions (admin only) */}
            {isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-border)] space-y-2"
              >
                <button
                  onClick={() => setShowAddLesson(true)}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors text-sm font-mono"
                >
                  <Plus className="w-4 h-4" />
                  {`ADD ${t.lesson.upper}`}
                </button>
                <button
                  onClick={handleDeleteCourse}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-mono"
                >
                  <Trash2 className="w-4 h-4" />
                  {`DELETE ${t.course.upper}`}
                </button>
              </motion.div>
            )}

            {/* Captain delete (cloned courses only) */}
            {!isAdmin && isCaptain && course.source_course_id && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-border)]"
              >
                <button
                  onClick={handleDeleteCourse}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-mono"
                >
                  <Trash2 className="w-4 h-4" />
                  {`DELETE ${t.course.upper}`}
                </button>
              </motion.div>
            )}
          </div>

          {/* Main content — right side */}
          <div className="col-span-12 lg:col-span-8">
            {/* Tabs */}
            <div className="flex border-b border-[var(--color-border)] mb-6">
              {[
                { key: 'lessons', label: `${t.lesson.titlePlural} (${course.lesson_count ?? lessons.length})`, tourId: 'lesson-list' },
                ...(isAdmin ? [
                  { key: 'settings', label: 'Settings', tourId: undefined },
                ] : []),
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as typeof activeTab)
                    if (tab.key === 'lessons') setSelectedRun(null)
                  }}
                  data-tour={tab.tourId}
                  className={`px-5 py-2.5 font-medium border-b-2 transition-colors text-sm ${
                    activeTab === tab.key
                      ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                      : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'lessons' && selectedRun && (
              <motion.div
                key={`run-${selectedRun.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Run header */}
                <div className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-primary-glow)] flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {editingRunTitle ? (
                        <form
                          onSubmit={(e) => { e.preventDefault(); handleRenameRun() }}
                          className="flex items-center gap-2"
                        >
                          <input
                            autoFocus
                            value={editRunTitleValue}
                            onChange={(e) => setEditRunTitleValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditingRunTitle(false) }}
                            className="text-lg font-bold bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg px-2 py-0.5 text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                          />
                          <button type="submit" disabled={savingRunTitle || !editRunTitleValue.trim()} className="p-1 rounded text-green-500 hover:bg-green-500/10 disabled:opacity-50" title="Save">
                            {savingRunTitle ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                          <button type="button" onClick={() => setEditingRunTitle(false)} className="p-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]" title="Cancel">
                            <X className="w-4 h-4" />
                          </button>
                        </form>
                      ) : (
                        <>
                          <h3 className="text-lg font-bold text-green-500">{selectedRun.title}</h3>
                          <button
                            onClick={() => { setEditRunTitleValue(selectedRun.title); setEditingRunTitle(true) }}
                            className="p-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)] transition-colors"
                            title="Rename run"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                        selectedRun.status === 'draft' ? 'bg-yellow-500/10 text-yellow-500'
                        : selectedRun.status === 'started' ? 'bg-yellow-500/10 text-yellow-500'
                        : selectedRun.status === 'completed' ? 'bg-[var(--color-input-bg)] text-[var(--color-text-muted)]'
                        : 'bg-red-500/10 text-red-500'
                      }`}>{selectedRun.status}</span>
                    </div>
                    <div className="text-sm text-[var(--color-text-muted)] flex items-center gap-4">
                      <span>{Math.max(selectedRun.enrolled_count ?? 0, ...(selectedRun.lessons ?? []).map((l: any) => l.player_count ?? 0))} students</span>
                      <span>{selectedRun.lesson_count ?? selectedRun.lessons?.length ?? 0} {t.lesson.lowerPlural}</span>
                      {selectedRun.student_tier && <span>Tier: {selectedRun.student_tier}</span>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3 shrink-0">
                    {selectedRun.run_code && (
                      <div className="text-right">
                        <div className="text-xs text-[var(--color-text-muted)] mb-1">Join Code</div>
                        <div className="font-mono text-lg font-bold text-[var(--color-primary)]">{selectedRun.run_code}</div>
                      </div>
                    )}
                    {selectedRun.status === 'completed' && (
                      <button
                        onClick={() => navigate(`/organizations/${organizationId}/courses/${courseId}/runs/${selectedRun.id}/report`)}
                        className="p-2 rounded-lg bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
                        title="View run report"
                      >
                        <BarChart2 className="w-4 h-4" />
                      </button>
                    )}
                    {selectedRun.status !== 'started' && (
                      <button
                        onClick={() => handleDeleteRun(selectedRun.id, selectedRun.title, selectedRun.status)}
                        className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                        title="Delete run"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Run QR code */}
                {selectedRun.run_code && (
                  <div className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-border)] flex items-center gap-4">
                    <QRCodeSVG
                      value={`${window.location.origin}/join/${selectedRun.run_code}`}
                      size={80}
                      bgColor="transparent"
                      fgColor="var(--color-text-muted)"
                    />
                    <div>
                      <div className="text-sm text-[var(--color-text)]">Students scan to join this course</div>
                      <div className="text-xs text-[var(--color-text-muted)] font-mono mt-1">
                        {window.location.origin}/join/{selectedRun.run_code}
                      </div>
                    </div>
                  </div>
                )}

                {/* Run lessons */}
                {(selectedRun.lessons ?? []).length === 0 ? (
                  <div className="text-center py-12 text-[var(--color-text-muted)]">
                    <Map className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">{`No ${t.lesson.lowerPlural} in this run`}</p>
                  </div>
                ) : (
                  (selectedRun.lessons ?? []).map((lesson, idx) => (
                    <motion.div
                      key={lesson.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      className="bg-[var(--color-surface)] rounded-xl p-4 border border-[var(--color-border)] flex items-center gap-4"
                    >
                      <div className="w-8 h-8 rounded-full bg-[var(--color-input-bg)] flex items-center justify-center text-sm font-mono text-[var(--color-text-muted)] shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[var(--color-primary)] truncate">{lesson.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs shrink-0 ${
                            lesson.status === 'live' || lesson.status === 'running'
                              ? 'bg-[var(--color-secondary-subtle)] text-[var(--color-secondary)]'
                              : lesson.status === 'completed'
                              ? 'bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                              : 'bg-[var(--color-warning-subtle)] text-[var(--color-warning)]'
                          }`}>
                            {lesson.status}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--color-text-muted)] mt-0.5 font-mono">
                          Code: {lesson.run_code}
                          <span className="ml-3">{lesson.player_count ?? 0} students</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => navigate(`/lessons/${lesson.id}/preview`)}
                          className="p-2 bg-[var(--color-input-bg)] text-[var(--color-text-muted)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
                          title="View activity builder"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {(lesson.status === 'completed' || lesson.status === 'live') && (
                          <button
                            onClick={() => navigate(`/lessons/${lesson.id}/report`)}
                            className="p-2 bg-purple-500/15 text-purple-500 rounded-lg hover:bg-purple-500/25 transition-colors"
                            title="View lesson report"
                          >
                            <BarChart2 className="w-4 h-4" />
                          </button>
                        )}
                        {selectedRun.status !== 'completed' && lesson.status !== 'completed' && (
                          <button
                            onClick={() => navigate(`/lesson/${lesson.id}?host=true`)}
                            className="p-2 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors"
                            title="Launch lesson"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            )}

            {activeTab === 'lessons' && !selectedRun && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                {isCatalogView ? (
                  /* Catalog view — captains browse lessons read-only */
                  lessons.length === 0 ? (
                    <div className="text-center py-16 text-[var(--color-text-muted)]">
                      <Map className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p className="text-lg font-medium">{`No ${t.lesson.lowerPlural} yet`}</p>
                    </div>
                  ) : (
                    lessons.map((lesson, idx) => (
                      <motion.div
                        key={lesson.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.04 }}
                        onClick={() => navigate(`/lessons/${lesson.id}/preview`)}
                        className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] p-4 flex items-center gap-4 cursor-pointer hover:bg-[var(--color-surface-hover)] transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-[var(--color-input-bg)] flex items-center justify-center text-sm font-mono text-[var(--color-text-muted)] shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-[var(--color-primary)] truncate block">{lesson.name}</span>
                          <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                            {(lesson.activities ?? []).length} {(lesson.activities ?? []).length === 1 ? 'activity' : 'activities'}
                          </div>
                        </div>
                        <Eye className="w-4 h-4 shrink-0 text-[var(--color-text-muted)]" />
                      </motion.div>
                    ))
                  )
                ) : !isAdmin && !isCatalogView ? (
                  /* Captain viewing their own cloned course with no run selected — prompt to create a run */
                  <div className="text-center py-16 text-[var(--color-text-muted)]">
                    <Map className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-lg font-medium">No runs yet</p>
                    <p className="text-sm mt-1">Create a run to get started with your {t.lesson.lowerPlural}</p>
                    <button
                      onClick={() => setShowNewRunModal(true)}
                      className="mt-4 px-4 py-2 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors text-sm"
                    >
                      Create First Run
                    </button>
                  </div>
                ) : lessons.length === 0 ? (
                  <div className="text-center py-16 text-[var(--color-text-muted)]">
                    <Map className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="text-lg font-medium">{`No ${t.lesson.lowerPlural} yet`}</p>
                    <p className="text-sm mt-1">{`Add ${t.lesson.lowerPlural} to build your ${t.course.lower}`}</p>
                    <button
                      onClick={() => setShowAddLesson(true)}
                      className="mt-4 px-4 py-2 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors text-sm"
                    >
                      {`Add First ${t.lesson.title}`}
                    </button>
                  </div>
                ) : (
                  /* Admin view — draggable lesson management */
                  lessons.map((lesson, idx) => (
                    <motion.div
                      key={lesson.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.04 }}
                      draggable={isAdmin}
                      onDragStart={e => handleDragStart(e, lesson.id)}
                      onDragOver={handleDragOver}
                      onDragEnter={() => handleDragEnter(lesson.id)}
                      onDrop={e => handleDrop(e, lesson.id)}
                      onDragEnd={handleDragEnd}
                      className={`bg-[var(--color-surface)] rounded-xl p-4 border flex items-center gap-4 transition-all ${
                        dragOverId === lesson.id && dragId !== lesson.id
                          ? 'border-green-500 bg-green-900/10'
                          : dragId === lesson.id
                          ? 'border-[var(--color-border)] opacity-50'
                          : 'border-[var(--color-border)]'
                      }`}
                    >
                      {isAdmin && (
                        <div className="cursor-grab active:cursor-grabbing text-[var(--color-muted)] hover:text-[var(--color-text-muted)] shrink-0">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}
                      <div className="w-8 h-8 rounded-full bg-[var(--color-input-bg)] flex items-center justify-center text-sm font-mono text-[var(--color-text-muted)] shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[var(--color-primary)] truncate">{lesson.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs shrink-0 ${
                            lesson.status === 'live' || lesson.status === 'running'
                              ? 'bg-[var(--color-secondary-subtle)] text-[var(--color-secondary)]'
                              : lesson.status === 'completed'
                              ? 'bg-[var(--color-success-subtle)] text-[var(--color-success)]'
                              : 'bg-[var(--color-warning-subtle)] text-[var(--color-warning)]'
                          }`}>
                            {lesson.status}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--color-text-muted)] mt-0.5 font-mono">
                          Code: {lesson.run_code}
                          <span className="ml-3">{lesson.player_count ?? 0} students</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {(lesson.status === 'completed' || lesson.status === 'live') && (
                          <button
                            onClick={() => navigate(`/lessons/${lesson.id}/report`)}
                            className="p-2 bg-purple-500/15 text-purple-500 rounded-lg hover:bg-purple-500/25 transition-colors"
                            title="View lesson report"
                          >
                            <BarChart2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/lessons/${lesson.id}/preview`)}
                          className="p-2 bg-amber-500/15 text-amber-500 rounded-lg hover:bg-amber-500/25 transition-colors"
                          title="Preview lesson"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if ((course?.credit_cost ?? 0) > 0 && !hasPaymentMethod) {
                              setShowPaymentModal(true)
                            } else {
                              navigate(`/lesson/${lesson.id}?host=true`)
                            }
                          }}
                          className="p-2 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors"
                          title="Launch lesson"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => navigate(`/lessons/${lesson.id}/edit`)}
                              className="p-2 bg-blue-500/15 text-blue-500 rounded-lg hover:bg-blue-500/25 transition-colors"
                              title="Edit lesson"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveLesson(lesson.id)}
                              className="p-2 bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                              title="Remove from course"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            )}

            {activeTab === 'settings' && isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[var(--color-surface)] rounded-xl p-6 border border-[var(--color-border)]"
              >
                <h2 className="text-lg font-semibold mb-6">{`${t.course.title} Settings`}</h2>
                <form onSubmit={handleSaveSettings} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">{`${t.course.title} Name`}</label>
                    <input
                      type="text"
                      required
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">Description</label>
                    <textarea
                      rows={4}
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)] resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">Self-Paced Enrollment Cost (credits per student)</label>
                    <input
                      type="number"
                      min={0}
                      value={editSelfPacedCreditCost}
                      onChange={(e) => setEditSelfPacedCreditCost(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">Set to 0 for free access. Students pay this many credits to self-enroll.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">Overage Cost (credits per extra student)</label>
                    <input
                      type="number"
                      min={0}
                      value={editCreditCost}
                      onChange={(e) => setEditCreditCost(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">Credits charged to captains per student exceeding their tier.</p>
                  </div>

                  {/* Instructor-led tier pricing */}
                  <div className="pt-2">
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Instructor-Led Tier Pricing</label>
                    <p className="text-xs text-[var(--color-text-muted)] mb-3">Credits charged per run based on student capacity. Set to 0 for free.</p>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        { tier: 25, value: editTier25Cost, setter: setEditTier25Cost },
                        { tier: 50, value: editTier50Cost, setter: setEditTier50Cost },
                        { tier: 100, value: editTier100Cost, setter: setEditTier100Cost },
                        { tier: 250, value: editTier250Cost, setter: setEditTier250Cost },
                      ] as const).map(({ tier, value, setter }) => (
                        <div key={tier} className="flex items-center gap-2">
                          <span className="text-sm text-[var(--color-text-muted)] w-20 shrink-0">{tier} students</span>
                          <input
                            type="number"
                            min={0}
                            value={value}
                            onChange={(e) => setter(parseInt(e.target.value) || 0)}
                            className="flex-1 px-3 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)] text-sm"
                            placeholder="credits"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">Estimated Duration (hours)</label>
                    <input
                      type="number"
                      min={0}
                      value={editDuration}
                      onChange={(e) => setEditDuration(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                      placeholder="e.g. 2"
                      className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">{`Shown on ${t.course.lower} cards. Leave empty if unknown.`}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">{`${t.course.title} Image`}</label>
                    {editImagePreview ? (
                      <div className="relative mb-2">
                        <img
                          src={editImagePreview}
                          alt="Course"
                          className="w-full rounded-lg border border-[var(--color-border)] max-h-[200px] object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => { setEditImageFile(null); setEditImagePreview(null) }}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center hover:bg-black/90 transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : null}
                    <label className="flex items-center gap-3 px-4 py-3 bg-[var(--color-input-bg)] border border-[var(--color-border)] border-dashed rounded-lg cursor-pointer hover:border-green-500 transition-colors">
                      <ImagePlus className="w-5 h-5 text-[var(--color-text-muted)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">{editImagePreview ? 'Replace image' : `Upload ${t.course.lower} image`}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setEditImageFile(file)
                            setEditImagePreview(URL.createObjectURL(file))
                          }
                        }}
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={savingSettings}
                    className="px-6 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:brightness-110 transition-all disabled:opacity-50"
                  >
                    {savingSettings ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Method Required Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="payment-modal-title">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--color-surface)] rounded-xl p-6 max-w-md w-full border border-[var(--color-border)]"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 id="payment-modal-title" className="text-lg font-semibold text-[var(--color-text)]">Payment method required</h3>
            </div>
            <p className="text-[var(--color-text)] text-sm mb-6">
              Add a card to your account before running this course. Students who enroll will be charged to your credit balance.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/add-payment-method', { state: { returnTo: location.pathname } })}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                Add payment method
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Lesson Modal */}
      {showAddLesson && (
        <AddLessonModal
          organizationId={organizationId!}
          courseId={courseId!}
          existingLessons={lessons}
          onClose={() => setShowAddLesson(false)}
          onSuccess={() => {
            setShowAddLesson(false)
            loadCourse()
          }}
        />
      )}

      {/* New Run Modal (title + tier selection) */}
      {showNewRunModal && course && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="new-run-modal-title">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-lg w-full"
          >
            {!showTierModal ? (
              /* Step 1: Run title entry */
              <>
                <h2 id="new-run-modal-title" className="text-lg font-bold text-[var(--color-text)] mb-1">New Run</h2>
                <p className="text-sm text-[var(--color-text-muted)] mb-4">
                  Give this run a title (e.g. &quot;Spring 2026 - Period 3&quot;)
                </p>
                <input
                  type="text"
                  value={newRunTitle}
                  onChange={(e) => setNewRunTitle(e.target.value)}
                  placeholder="Run title..."
                  className="w-full px-4 py-2.5 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none mb-4"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowNewRunModal(false); setNewRunTitle(''); setSelectedTier(null) }}
                    className="flex-1 px-4 py-2.5 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowTierModal(true)}
                    disabled={!newRunTitle.trim()}
                    className="flex-1 px-4 py-2.5 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:brightness-110 transition-all text-sm font-medium disabled:opacity-50"
                  >
                    Next: Select Tier
                  </button>
                </div>
              </>
            ) : showPaymentModal ? (
              /* Step 2b: Payment method required (shown when user tries to create without a card) */
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--color-text)]">Payment method required</h3>
                </div>
                <p className="text-[var(--color-text)] text-sm mb-6">
                  A card on file is required before creating a run. Any tier overages will be charged automatically.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-4 py-2.5 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => navigate('/add-payment-method', { state: { returnTo: location.pathname } })}
                    className="ds-btn ds-btn-primary ds-btn-sm flex-1"
                  >
                    Add payment method
                  </button>
                </div>
              </>
            ) : (
              /* Step 2a: Tier selection (always shown) */
              <>
                <h2 className="text-lg font-bold text-[var(--color-text)] mb-1">Select Student Tier</h2>
                <p className="text-sm text-[var(--color-text-muted)] mb-2">
                  Run: <span className="text-[var(--color-text)] font-medium">{newRunTitle}</span>
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mb-4">Your balance: <span className="text-[var(--color-secondary)] font-semibold">{creditBalance} credits</span></p>

                {(() => {
                  const tiers = [25, 50, 100, 250] as const
                  const affordability = tiers.map((tier) => {
                    const cost = course[`tier_${tier}_cost` as keyof Course] as number
                    return cost === 0 || creditBalance >= cost
                  })
                  const canAffordAny = affordability.some(Boolean)
                  const canAffordAll = affordability.every(Boolean)
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {tiers.map((tier) => {
                          const cost = course[`tier_${tier}_cost` as keyof Course] as number
                          const canAfford = cost === 0 || creditBalance >= cost
                          const isSelected = selectedTier === tier
                          return (
                            <button
                              key={tier}
                              onClick={() => setSelectedTier(tier)}
                              disabled={!canAfford}
                              className={`p-4 rounded-lg border-2 text-left transition-all ${
                                isSelected
                                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-subtle)]'
                                  : canAfford
                                    ? 'border-[var(--color-border)] bg-[var(--color-surface-deep)] hover:border-[var(--color-text-muted)]'
                                    : 'border-[var(--color-border)] bg-[var(--color-surface)] opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className="text-xl font-bold text-[var(--color-text)]">{tier}</div>
                              <div className="text-xs text-[var(--color-text-muted)]">students</div>
                              <div className={`text-sm font-semibold mt-2 ${cost === 0 ? 'text-[var(--color-primary)]' : 'text-[var(--color-secondary)]'}`}>
                                {cost === 0 ? 'FREE' : `${cost} credits`}
                              </div>
                              {!canAfford && (
                                <div className="text-xs text-red-600 dark:text-red-400 mt-1">Insufficient credits</div>
                              )}
                            </button>
                          )
                        })}
                      </div>

                      {course.credit_cost > 0 && (
                        <p className="text-xs text-[var(--color-text-muted)] mb-4">
                          If enrollment exceeds your selected tier, {course.credit_cost} credit(s) per additional student will be charged automatically.
                        </p>
                      )}

                      {!canAffordAny ? (
                        <button
                          onClick={() => navigate('/users/plans')}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-4 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:brightness-110 transition-all text-sm font-medium"
                        >
                          <Coins className="w-4 h-4" />
                          Top Up Credits
                        </button>
                      ) : !canAffordAll && (
                        <button
                          onClick={() => navigate('/users/plans')}
                          className="flex items-center justify-center gap-1.5 mx-auto mb-4 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                        >
                          <Coins className="w-3 h-3" />
                          Need more credits? Top up
                        </button>
                      )}

                      <div className="flex gap-3">
                        <button
                          onClick={() => { setShowTierModal(false); setSelectedTier(null) }}
                          className="flex-1 px-4 py-2.5 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
                        >
                          Back
                        </button>
                        <button
                          onClick={() => {
                            if (!hasPaymentMethod) {
                              setShowPaymentModal(true)
                              return
                            }
                            handleCreateRun(selectedTier ?? undefined)
                          }}
                          disabled={creatingRun || !selectedTier}
                          className="flex-1 px-4 py-2.5 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:brightness-110 transition-all text-sm font-medium disabled:opacity-50"
                        >
                          {creatingRun ? 'Creating...' : `Create with ${selectedTier ?? '...'} students`}
                        </button>
                      </div>
                    </>
                  )
                })()}
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Clone Success Modal */}
      {showCloneSuccessModal && (
        <div className={pirateStyles.creditsModal} role="dialog" aria-modal="true" aria-labelledby="clone-success-title">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={pirateStyles.creditsModalContent}
          >
            <div className={pirateStyles.creditsModalHeader}>
              <div className={pirateStyles.successModalIcon}>
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 id="clone-success-title" className={pirateStyles.creditsModalTitle}>
                {clonedCourseId ? `${t.course.title} Unlocked!` : 'Already in My Courses'}
              </h3>
            </div>
            <p className={pirateStyles.creditsModalText}>
              {clonedCourseId
                ? `You've successfully gained access to this ${t.course.lower}. It's now in your My ${t.course.titlePlural} — create runs, manage lessons, and make it your own.`
                : `You already have access to this ${t.course.lower}. Head to My ${t.course.titlePlural} to manage it.`
              }
            </p>
            <div className={pirateStyles.creditsModalActions}>
              <button
                onClick={() => {
                  setShowCloneSuccessModal(false)
                  if (clonedCourseId) {
                    navigate(`/organizations/${organizationId}/courses/${clonedCourseId}`, { state: { from: 'my-courses' } })
                  } else {
                    navigate('/my-courses')
                  }
                }}
                className={pirateStyles.successModalBtn}
              >
                Go to My {t.course.titlePlural}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Run Confirmation Modal */}
      {showDeleteRunModal && deleteRunTarget && (
        <div className={pirateStyles.creditsModal} role="dialog" aria-modal="true" aria-labelledby="delete-run-title">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={pirateStyles.creditsModalContent}
          >
            <div className={pirateStyles.creditsModalHeader}>
              <div className={pirateStyles.creditsModalIcon}>
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 id="delete-run-title" className={pirateStyles.creditsModalTitle}>
                Delete Run
              </h3>
            </div>
            <p className={pirateStyles.creditsModalText}>
              Are you sure you want to delete <strong>"{deleteRunTarget.title}"</strong>?
              {deleteRunTarget.status !== 'completed' && (
                <> This run has not been completed.</>
              )}
              {' '}This will permanently remove all associated data including student responses.
              Any credits already charged for this run will not be reversed.
            </p>
            <div className={pirateStyles.creditsModalActions}>
              <button
                onClick={() => {
                  setShowDeleteRunModal(false)
                  setDeleteRunTarget(null)
                }}
                disabled={deletingRun}
                className={pirateStyles.creditsModalCancel}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRun}
                disabled={deletingRun}
                className={pirateStyles.deleteModalBtn}
              >
                {deletingRun ? 'Deleting...' : 'Delete Run'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

function AddLessonModal({
  organizationId,
  courseId,
  existingLessons,
  onClose,
  onSuccess,
}: {
  organizationId: string
  courseId: string
  existingLessons: Lesson[]
  onClose: () => void
  onSuccess: () => void
}) {
  const t = useTerminology()
  const [orgLessons, setOrgLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.getLessons({ page: 1 })
        const all: Lesson[] = res.data ?? []
        const existingIds = new Set(existingLessons.map(l => l.id))
        setOrgLessons(all.filter(l => !existingIds.has(l.id)))
      } catch (e) {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleAdd = async (lessonId: string) => {
    setAdding(true)
    try {
      await apiClient.addLessonToCourse(organizationId, courseId, lessonId)
      showSuccessToast(`${t.lesson.title} added to ${t.course.lower}!`)
      onSuccess()
    } catch (err: any) {
      handleApiError(err, 'add lesson')
      setAdding(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] p-6 w-full max-w-lg">
        <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4 font-mono">{`ADD ${t.lesson.upper}`}</h2>

        {loading ? (
          <div className="text-center py-8 text-[var(--color-text-muted)]">{`Loading ${t.lesson.lowerPlural}...`}</div>
        ) : orgLessons.length === 0 ? (
          <p className="text-[var(--color-text-muted)] text-sm py-8 text-center">
            {`No available ${t.lesson.lowerPlural} to add. Create a ${t.lesson.lower} from the ${t.course.titlePlural} page first.`}
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {orgLessons.map(lesson => (
              <div
                key={lesson.id}
                className="flex items-center justify-between bg-[var(--color-input-bg)] rounded-lg p-3"
              >
                <div>
                  <div className="font-medium text-[var(--color-text)]">{lesson.name}</div>
                  <div className="text-xs text-[var(--color-text-muted)] font-mono">{lesson.run_code}</div>
                </div>
                <button
                  onClick={() => handleAdd(lesson.id)}
                  disabled={adding}
                  className="px-3 py-1.5 bg-green-500/15 text-green-500 rounded-lg hover:bg-green-500/25 transition-colors text-sm disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
